import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { FormLink, formLinkFromQueryParam } from "@/lib/misc/formLink";
import * as assert from "typed-assert";

export function useFormRouter(): {
  goBack: () => void;
  goToPage: (page: string) => void;
  replaceWithPage: (page: string) => void;
  replaceWithLocale: (page: string) => void;
  prefetchPage: (page: string) => void;
  getCurrentLocale: () => string;
  getFormLink: () => FormLink;
  formLinkFromQueryParam: (
    instance: string | string[] | undefined
  ) => FormLink;
} {
  const router = useRouter();

  const getCurrentLocale = () => String(router.query.locale);

  const processInstance = (instance: string | string[] | undefined): string => {
    const instanceString = Array.isArray(instance)
      ? instance.join(".")
      : instance;

    return instanceString ? `?i=${instanceString}` : "";
  };

  const getPageUrl = useCallback(
    (page: string) => {
      const { digitalFormId, locale, i: instance } = router.query;

      const instanceToAppend = processInstance(instance);

      assert.isNotUndefined(locale);
      const localeStr = Array.isArray(locale) ? locale[0] : locale;
      const pathPrefix = (router.pathname.match("(^/preview)") || [""])[0];
      const suffix =
        page === "index"
          ? `${instanceToAppend}`
          : `${page}/${instanceToAppend}`;
      if (pathPrefix.length === 0) {
        // published or dev
        return `/${localeStr}/${suffix}`;
      }

      // preview
      assert.isNotUndefined(digitalFormId);
      const digitalFormIdStr = Array.isArray(digitalFormId)
        ? digitalFormId[0]
        : digitalFormId;
      return `${pathPrefix}/${digitalFormIdStr}/${localeStr}/${suffix}`;
    },
    [router.pathname, router.query]
  );

  const goBack = useCallback(() => {
    router.back();
  }, [router]);

  const goToPage = useCallback(
    (page: string) => {
      router.push(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const replaceWithPage = useCallback(
    (page: string) => {
      router.replace(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const prefetchPage = useCallback(
    (page: string) => {
      router.prefetch(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const replaceWithLocale = useCallback(
    (locale: string) => {
      const {
        pathname,
        query: { i: instance },
      } = router;

      const instanceToAppend = processInstance(instance);

      router.replace(
        pathname
          .replace("[digitalFormId]", String(router.query.digitalFormId))
          .replace("[locale]", locale)
          .concat(instanceToAppend)
      );
    },
    [router]
  );

  const formLink = useMemo(
    () => formLinkFromQueryParam(router.query.i),
    [router.query.i]
  );
  const getFormLink = () => formLink;

  return {
    goBack,
    goToPage,
    replaceWithPage,
    replaceWithLocale,
    prefetchPage,
    getCurrentLocale,
    getFormLink,
    formLinkFromQueryParam,
  };
}
